import { useId, useMemo, useState, type KeyboardEvent } from 'react'
import Icon from '../docs/Icon'
import Dialog from '../ui/Dialog'
import AgencyMark from '../marks/AgencyMark'
import { ancestorsOf, missions, parentOf, type Mission } from '@/content/missions'
import { usePrefs } from '@/state/prefs'
import { useCasing } from '@/state/useCasing'

type Group = 'nation' | 'department' | 'independent' | 'financial' | 'intelligence'

const groups: Array<{ kind: Group; label: string }> = [
  { kind: 'nation', label: 'United States' },
  { kind: 'department', label: 'Executive departments' },
  { kind: 'independent', label: 'Independent agencies' },
  { kind: 'financial', label: 'Financial regulators' },
  { kind: 'intelligence', label: 'Intelligence Community' },
]

/** Extra words that find a community's members in search. */
const communityNames: Record<NonNullable<Mission['community']>, string> = {
  intelligence: 'Intelligence Community IC',
  financial: 'Financial regulators',
}

/**
 * The group a mission is listed under: Intelligence Community members and
 * financial regulators sit together, and other sub-agencies sit with their
 * departments.
 */
const groupOf = (mission: Mission): Group =>
  mission.community ?? (mission.kind === 'subagency' ? 'department' : mission.kind)

/**
 * Missions in display order: the nation, each department followed by its
 * sub-agencies and their offices, the independent agencies, the financial
 * regulators, then the Intelligence Community. Arrow keys follow this order.
 */
function displayOrder(list: Mission[]) {
  const ordered: Mission[] = []
  const withChildren = (mission: Mission) => {
    ordered.push(mission)
    for (const child of list) if (child.parent === mission.id && groupOf(child) === 'department') withChildren(child)
  }
  for (const { kind } of groups) {
    for (const mission of list) {
      if (groupOf(mission) !== kind || (kind === 'department' && mission.kind === 'subagency')) continue
      if (kind === 'department') withChildren(mission)
      else ordered.push(mission)
    }
  }
  // Sub-agencies whose department did not match the search still appear.
  for (const mission of list) if (!ordered.includes(mission)) ordered.push(mission)
  return ordered
}

type MissionPickerProps = {
  open: boolean
  onClose: () => void
  onSelect?: (mission: Mission) => void
}

/** A searchable list of every mission theme, in a dialog. */
export default function MissionPicker({ open, onClose, onSelect }: MissionPickerProps) {
  const { prefs, set, reset } = usePrefs()
  const [confirming, setConfirming] = useState(false)
  const cased = useCasing()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const listId = useId()

  const results = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
    return displayOrder(
      missions.filter((mission) => {
        const family = ancestorsOf(mission).map((ancestor) => `${ancestor.name} ${ancestor.short}`)
        const community = mission.community ? communityNames[mission.community] : ''
        const haystack = `${mission.name} ${mission.short} ${family.join(' ')} ${community}`
        return words.every((word) => haystack.toLowerCase().includes(word))
      }),
    )
  }, [query])

  const choose = (mission: Mission) => {
    set('mission', mission.id)
    onSelect?.(mission)
    setQuery('')
    onClose()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((index) => Math.min(results.length - 1, index + 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((index) => Math.max(0, index - 1))
    } else if (event.key === 'Enter' && results[active]) {
      event.preventDefault()
      choose(results[active])
    }
  }

  const optionId = (mission: Mission) => `${listId}-${mission.id}`

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={cased('Choose your mission')}
      className="mds-mission-picker"
      actions={
        <button
          type="button"
          className="mds-button mds-button--ghost mds-button--sm"
          aria-expanded={confirming}
          onClick={() => setConfirming((state) => !state)}
        >
          <Icon name="reset" size={16} />
          {cased('Reset all styles')}
        </button>
      }
    >
      {/* A full reset sits by the close button: every style choice back to the
          MDS defaults, after a second press. Light or dark stays as chosen. */}
      {confirming && (
        <div className="mds-mission-picker__reset" role="group" aria-label="Reset styles">
          <span>{cased('Reset every style to the MDS defaults?')}</span>
          <span className="mds-cluster">
            <button
              type="button"
              className="mds-button mds-button--primary mds-button--sm"
              onClick={() => {
                reset()
                setConfirming(false)
                setQuery('')
                onClose()
              }}
            >
              {cased('Reset')}
            </button>
            <button type="button" className="mds-button mds-button--ghost mds-button--sm" onClick={() => setConfirming(false)} autoFocus>
              {cased('Cancel')}
            </button>
          </span>
        </div>
      )}
      <input
        className="mds-input"
        role="combobox"
        aria-label="Search missions"
        aria-expanded="true"
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={results[active] ? optionId(results[active]) : undefined}
        placeholder="Search agencies"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value)
          setActive(0)
        }}
        onKeyDown={onKeyDown}
        data-autofocus
      />
      <div id={listId} role="listbox" aria-label="Missions" className="mds-mission-list">
        {results.length === 0 && <p className="mds-muted">No agency matches “{query}”.</p>}
        {groups.map(({ kind, label }) => {
          const items = results.filter((mission) => groupOf(mission) === kind)
          if (!items.length) return null
          return (
            <div key={kind} role="group" aria-label={label} className="mds-mission-list__group">
              <p className="mds-eyebrow" aria-hidden="true">
                {label}
              </p>
              {items.map((mission) => {
                const index = results.indexOf(mission)
                return (
                  <div
                    key={mission.id}
                    id={optionId(mission)}
                    role="option"
                    aria-selected={prefs.mission === mission.id}
                    className={`mds-mission-option${index === active ? ' is-active' : ''}${mission.parent && groupOf(mission) === 'department' ? ` mds-mission-option--child${ancestorsOf(mission).length > 1 ? ' mds-mission-option--grandchild' : ''}` : ''}`}
                    data-mission={mission.id}
                    onClick={() => choose(mission)}
                    onMouseEnter={() => setActive(index)}
                  >
                    <AgencyMark mission={mission} variant={prefs.mark} className="mds-mission-option__mark" />
                    <span className="mds-mission-option__name">
                      {mission.name}
                      <span className="mds-muted">
                        {' '}
                        {mission.short}
                        {mission.parent && <span className="mds-visually-hidden">, part of {parentOf(mission)?.name}</span>}
                      </span>
                    </span>
                    <span className="mds-mission-option__swatches" aria-hidden="true">
                      <span style={{ background: 'var(--mds-primary)' }} />
                      <span style={{ background: 'var(--mds-accent)' }} />
                    </span>
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
    </Dialog>
  )
}
